import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  orderBy,
  query,
  limit,
  onSnapshot,
  serverTimestamp,
  where
} from "firebase/firestore";
import { db } from "@/firebase";
// DateUtils no longer needed - using template filters for date formatting

const state = {
  offerList: [],
  currentOffer: null,
  loadingCurrentOffer: false,
  pendingOfferUpdates: {},
  processingOffer: false,
  currentOfferEntries: [],
  loadingEntries: false,
  entriesUnsubscribe: null, // Add this to store the unsubscribe function
  error: null
};

const actions = {
  async getOfferList({ commit, getters }, user) {
    if (!user) {
      commit("setOfferList", []);
      return [];
    }

    commit("setLoadingOffers", true);
    commit("setOfferError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);

      // First, fetch all offers
      const offersRef = collection(programRef, "offers");
      const offersQuery = query(offersRef, orderBy("order"));
      const offersSnapshot = await getDocs(offersQuery);

      // Create a map of all offers by ID for quick lookup
      const offersMap = {};
      const regularOffers = [];

      // Process each offer and filter by member/company tags
      offersSnapshot.forEach(docSnap => {
        const offerData = docSnap.data();
        const offer = {
          ...offerData,
          offerId: docSnap.id,
          cardType: "offer"
          // Keep created/updated as Firebase timestamps - will be formatted in template with filters
        };
        // Store all offers in the map for later use with groups
        offersMap[docSnap.id] = offer;

        // Only add active offers that match the user's tags to the regular offers list
        if (
          (offerData.status === "Active" || offerData.status === "active") &&
          isOfferVisibleToUser(offer, user)
        ) {
          regularOffers.push(offer);
        }
      });

      // Next, fetch advanced offer groups
      const groupsRef = collection(programRef, "advancedOfferGroups");
      const groupsQuery = query(groupsRef, orderBy("order"));
      const groupsSnapshot = await getDocs(groupsQuery);

      // Process groups and create consolidated offers
      const consolidatedOffers = [];
      // Track all opportunity IDs that are included in consolidated offers
      const consolidatedOpportunityIds = new Set();
      // Track all offer IDs that have at least one opportunity in a consolidated group
      const offersInConsolidatedGroups = new Set();

      groupsSnapshot.forEach(docSnap => {
        const groupData = docSnap.data();

        // Skip groups with no offers
        if (
          !groupData.offers ||
          !Array.isArray(groupData.offers) ||
          groupData.offers.length === 0
        ) {
          return;
        }

        // Collect all valid offers in this group
        const groupOffers = [];
        const allOpportunities = [];

        groupData.offers.forEach(offerId => {
          const offer = offersMap[offerId];

          // Skip if offer doesn't exist or isn't visible to user
          if (!offer || !isOfferVisibleToUser(offer, user)) {
            return;
          }

          groupOffers.push(offer);

          // Add all opportunities from this offer to our consolidated list
          if (offer.opportunities && Array.isArray(offer.opportunities)) {
            offer.opportunities.forEach(opportunity => {
              // Add the parent offer ID to each opportunity for reference
              allOpportunities.push({
                ...opportunity,
                parentOfferId: offerId
              });

              // Track this opportunity ID
              if (opportunity.id) {
                consolidatedOpportunityIds.add(opportunity.id);
                // Also track the parent offer
                offersInConsolidatedGroups.add(offerId);
              }
            });
          }
        });

        // Only create a consolidated offer if there are offers in the group visible to the user
        if (groupOffers.length > 0) {
          // Create a consolidated offer from the group
          const consolidatedOffer = {
            offerId: docSnap.id,
            groupId: docSnap.id,
            title: groupData.groupName,
            displayTitle: groupData.groupName,
            titleUppercase: groupData.groupName.toUpperCase(),
            status: "Active",
            cardType: "offerGroup",
            created: groupData.createdAt, // Keep as Firebase timestamp
            updated: groupData.updatedAt, // Keep as Firebase timestamp
            order: groupData.order !== undefined ? groupData.order : 0,
            // Include all opportunities from all offers in the group
            opportunities: allOpportunities,
            // Add a reference to the original offers that make up this group
            sourceOffers: groupOffers.map(o => o.offerId),
            // Use empty arrays for tags to avoid filtering issues
            memberTags: [],
            companyTags: [],
            // Flag indicating this is a consolidated group
            isAdvancedGroup: true
          };

          consolidatedOffers.push(consolidatedOffer);
        }
      });

      // Filter out regular offers that have all their opportunities in consolidated groups
      const filteredRegularOffers = regularOffers.filter(offer => {
        // If this offer doesn't have any opportunities in consolidated groups, keep it
        if (!offersInConsolidatedGroups.has(offer.offerId)) {
          return true;
        }

        // If the offer has any opportunities that aren't in consolidated groups, keep it
        if (offer.opportunities && Array.isArray(offer.opportunities)) {
          // Create a filtered copy of opportunities that aren't in consolidated groups
          const remainingOpportunities = offer.opportunities.filter(
            opp => !consolidatedOpportunityIds.has(opp.id)
          );

          // If there are remaining opportunities, keep this offer but with filtered opportunities
          if (remainingOpportunities.length > 0) {
            // Replace the opportunities with only those not in consolidated groups
            offer.opportunities = remainingOpportunities;
            return true;
          }
        }

        // If all opportunities are in consolidated groups, remove this offer from regular offers
        return false;
      });

      // Combine filtered regular offers with consolidated group offers
      const finalOfferList = [...filteredRegularOffers, ...consolidatedOffers];

      // Sort by order
      finalOfferList.sort((a, b) => {
        // First sort by order
        if (a.order !== b.order) {
          return a.order - b.order;
        }
        // If order is the same, sort by title
        return a.title.localeCompare(b.title);
      });
      commit("setOfferList", finalOfferList);

      return finalOfferList;
    } catch (error) {
      console.error("Error fetching offers:", error);
      commit("setOfferError", error.message);
      commit("setOfferList", []);
      return [];
    } finally {
      commit("setLoadingOffers", false);
    }
  },

  async submitEntry({ commit, getters }, payload) {
    if (!payload || !payload.offerId || !payload.data) {
      throw new Error("Offer ID and entry data are required");
    }

    commit("setOfferError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const offerRef = doc(programRef, "offers", payload.offerId);
      const offerDoc = await getDoc(offerRef);

      if (!offerDoc.exists()) {
        throw new Error("Offer not found");
      }

      const offer = { ...offerDoc.data() };
      const entries = offer.entries || [];
      const newEntries = [...entries, payload.data];

      await updateDoc(offerRef, {
        entries: newEntries,
        updated: serverTimestamp(),
        updatedBy: getters.currentUser?._id || "system"
      });

      return true;
    } catch (error) {
      console.error("Error submitting entry:", error);
      commit("setOfferError", error.message);
      throw error;
    }
  },

  async delEntry({ commit, getters }, payload) {
    if (!payload || !payload.offerId || !payload.entry) {
      throw new Error("Offer ID and entry are required");
    }

    commit("setOfferError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const offerRef = doc(programRef, "offers", payload.offerId);
      const offerDoc = await getDoc(offerRef);

      if (!offerDoc.exists()) {
        throw new Error("Offer not found");
      }

      const offer = { ...offerDoc.data() };
      const entries = offer.entries || [];
      const newEntries = entries.filter(entry => entry.id !== payload.entry.id);

      await updateDoc(offerRef, {
        entries: newEntries,
        updated: serverTimestamp(),
        updatedBy: getters.currentUser?._id || "system"
      });

      return true;
    } catch (error) {
      console.error("Error deleting entry:", error);
      commit("setOfferError", error.message);
      throw error;
    }
  },

  async loadCurrentOffer({ commit, getters }, offerId) {
    if (!offerId) {
      throw new Error("Offer ID is required");
    }

    commit("setLoadingCurrentOffer", true);
    commit("setCurrentOffer", null);
    commit("setOfferError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const offerRef = doc(programRef, "offers", offerId);
      const offerDoc = await getDoc(offerRef);

      if (!offerDoc.exists()) {
        throw new Error("No such Offer");
      }

      const offerData = offerDoc.data();

      // Keep dates as Firebase timestamps - will be formatted in template with filters

      // Keep opportunity deadlines as Firebase timestamps - will be formatted in template with filters

      const processedOffer = { id: offerId, ...offerData };
      commit("setCurrentOffer", processedOffer);
      return processedOffer;
    } catch (error) {
      console.error("Error loading current offer:", error);
      commit("setOfferError", error.message);
      throw error;
    } finally {
      commit("setLoadingCurrentOffer", false);
    }
  },

  async saveFormEntry({ commit, getters, dispatch }, payload) {
    if (!payload || !payload.formData) {
      throw new Error("Form data is required");
    }

    commit("setOfferError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);

      // Determine which offer ID to use
      const offerId = payload.parentOfferId || payload.offerId;
      if (!offerId) {
        throw new Error("No valid offer ID provided");
      }

      const offerRef = doc(programRef, "offers", offerId);
      const entriesRef = collection(offerRef, "entries");

      // Let Firestore auto-generate the document ID unless we're editing an existing entry
      const entryId = payload.entryId || null;

      // Clean form data using JSON serialization to remove Vue observers
      const cleanFormData = JSON.parse(JSON.stringify(payload.formData));

      // Check if form data is too large (Firestore has 1MB limit per document)
      const formDataSize = JSON.stringify(cleanFormData).length;
      if (formDataSize > 900000) {
        // Leave some buffer for other fields
        throw new Error(
          `Form data too large: ${formDataSize} bytes. Maximum allowed is ~900KB.`
        );
      }

      // Additional validation checks
      if (!getters.programId) {
        throw new Error(
          "Program ID is missing - user may not be properly authenticated"
        );
      }

      if (!offerId) {
        throw new Error("Offer ID is missing");
      }

      // Validate required payload fields
      if (!payload.memberId) {
        console.warn("Member ID is missing from payload");
      }

      if (!payload.opportunityId) {
        console.warn("Opportunity ID is missing from payload");
      }

      const entryData = {
        // Don't include id field - let Firestore auto-generate it
        offerId: offerId,
        opportunity: payload.opportunityId || "",
        member: payload.memberId || "",
        status: "Pending",
        redeemable: true,
        reviewed: "", // Keep this field - it will be set to a timestamp when admin reviews the entry
        points: 0,
        totalPoints: 0,
        formPoints: payload.formData.formPoints || 0,
        bonusPoints: 0, // Ensure this stays as 0, not null
        submitted: serverTimestamp(),
        formSubmittedAt: serverTimestamp(),
        lastModifiedAt: serverTimestamp(),
        company: payload.company || "",
        addToDatabucket: true,
        history: [],
        formSubmissionData: cleanFormData,
        createdBy: getters.currentUser?._id || "anonymous",
        updatedBy: getters.currentUser?._id || "anonymous",
        // Cascade deadline from opportunity if available, use empty string instead of null
        deadline: payload.deadline || ""
      };

      // No need to process dates since we're using serverTimestamp()
      const processedEntryData = entryData;

      // Skip ID validation since Firestore will auto-generate it

      if (
        !processedEntryData.offerId ||
        typeof processedEntryData.offerId !== "string"
      ) {
        throw new Error("Invalid offer ID");
      }

      if (
        !processedEntryData.status ||
        typeof processedEntryData.status !== "string"
      ) {
        throw new Error("Invalid entry status");
      }

      // Check for any undefined values that might cause issues
      const hasUndefinedValues = Object.values(processedEntryData).some(
        value => value === undefined
      );
      if (hasUndefinedValues) {
        // Remove undefined values
        Object.keys(processedEntryData).forEach(key => {
          if (processedEntryData[key] === undefined) {
            delete processedEntryData[key];
          }
        });
      }

      // Check for any problematic values (excluding serverTimestamp objects which are valid)
      const problematicFields = [];
      Object.entries(processedEntryData).forEach(([key, value]) => {
        if (value === undefined) {
          problematicFields.push(`${key}: undefined`);
        } else if (typeof value === "function") {
          problematicFields.push(`${key}: function`);
        } else if (
          value &&
          typeof value === "object" &&
          value.constructor &&
          value.constructor.name !== "Object" &&
          value.constructor.name !== "Array" &&
          value.constructor.name !== "Date" &&
          value._methodName !== "serverTimestamp" // Exclude serverTimestamp objects
        ) {
          problematicFields.push(`${key}: ${value.constructor.name} object`);
        }
      });

      // Check for empty strings in critical fields
      const criticalFields = ["id", "offerId", "status"];
      const emptyFields = [];
      criticalFields.forEach(field => {
        if (
          processedEntryData[field] === "" ||
          processedEntryData[field] === null
        ) {
          emptyFields.push(field);
        }
      });

      if (emptyFields.length > 0) {
        throw new Error(
          `Critical fields cannot be empty: ${emptyFields.join(", ")}`
        );
      }

      // Validate data can be serialized to JSON
      try {
        JSON.stringify(processedEntryData);
      } catch (error) {
        console.error("❌ Data serialization failed:", error);
        throw new Error("Form data contains non-serializable values");
      }

      // Use addDoc to let Firestore auto-generate the ID, or setDoc if editing existing
      let docRef;
      if (entryId) {
        // Editing existing entry
        docRef = doc(entriesRef, entryId);
        await setDoc(docRef, processedEntryData);
      } else {
        // Creating new entry - let Firestore auto-generate ID
        docRef = await addDoc(entriesRef, processedEntryData);
      }

      const finalEntryId = docRef.id;

      // Add the new entry to the current entries array with the actual ID
      const entryWithId = { ...entryData, id: finalEntryId };
      commit("addEntryToCurrentOfferEntries", entryWithId);
      dispatch("setSnackbar", "Entry submitted successfully");

      return finalEntryId;
    } catch (error) {
      console.error("Error saving form entry:", error);

      commit("setOfferError", error.message);
      throw error;
    }
  },

  async fetchOfferEntries({ commit, getters, state }, offerId) {
    if (!offerId) {
      throw new Error("Offer ID is required");
    }

    // Unsubscribe from any existing listener
    if (state.entriesUnsubscribe) {
      state.entriesUnsubscribe();
    }

    commit("setLoadingEntries", true);
    commit("setOfferError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const offerRef = doc(programRef, "offers", offerId);
      const entriesRef = collection(offerRef, "entries");

      // Use a one-time get instead of a real-time listener for better performance
      const entriesQuery = query(
        entriesRef,
        orderBy("submitted", "desc"),
        limit(50)
      );
      const entriesSnapshot = await getDocs(entriesQuery);

      if (entriesSnapshot.empty) {
        commit("setCurrentOfferEntries", []);
        return [];
      }

      const expandedEntries = [];
      const processedIds = new Set();

      // Process all base entries
      entriesSnapshot.docs.forEach(docSnap => {
        const data = docSnap.data();
        const entry = {
          ...data,
          _id: docSnap.id
          // Keep all timestamps as Firebase timestamps - will be formatted in template with filters
        };

        // If the entry has history, create individual "expanded" entries for each history item
        if (
          entry.history &&
          Array.isArray(entry.history) &&
          entry.history.length > 0
        ) {
          entry.history.forEach(historyItem => {
            if (historyItem) {
              // Create a unique ID for the history entry
              const historyEntryId = `${entry.id ||
                entry._id}_${historyItem.id ||
                Math.random()
                  .toString(36)
                  .substr(2, 9)}`;

              // Skip if we've already processed this ID
              if (processedIds.has(historyEntryId)) {
                return;
              }

              processedIds.add(historyEntryId);

              // Create a new entry object based on the history item
              const historyEntry = {
                ...entry,
                _id: historyEntryId,
                id: historyEntryId,
                status: historyItem.status || entry.status,
                points: historyItem.points || 0,
                totalPoints: historyItem.total || 0,
                comments: historyItem.comments || "",
                isHistoryItem: true,
                historyItemId: historyItem.id || "",
                parentEntryId: entry.id,
                submitted: historyItem.created || entry.submitted,
                reviewed: historyItem.reviewed || null,
                reviewedBy: historyItem.reviewedBy || entry.reviewedBy || ""
              };

              expandedEntries.push(historyEntry);
            }
          });
        } else {
          // If no history, include the main entry as is
          if (!processedIds.has(entry.id)) {
            processedIds.add(entry.id);
            expandedEntries.push({
              ...entry,
              isHistoryItem: false
            });
          }
        }
      });

      commit("setCurrentOfferEntries", expandedEntries);

      // Optional: Set up a lightweight listener for new entries only
      const newEntriesQuery = query(
        entriesRef,
        orderBy("submitted", "desc"),
        limit(5)
      );

      const unsubscribe = onSnapshot(newEntriesQuery, snapshot => {
        if (!snapshot.empty && snapshot.docChanges().length > 0) {
          // Only refresh if there are actual changes
          const refreshQuery = query(
            entriesRef,
            orderBy("submitted", "desc"),
            limit(50)
          );

          getDocs(refreshQuery)
            .then(newSnapshot => {
              const newExpandedEntries = [];
              const newProcessedIds = new Set();

              newSnapshot.docs.forEach(docSnap => {
                const data = docSnap.data();
                const entry = {
                  ...data,
                  _id: docSnap.id
                  // Keep all timestamps as Firebase timestamps - will be formatted in template with filters
                };

                // Process history items
                if (
                  entry.history &&
                  Array.isArray(entry.history) &&
                  entry.history.length > 0
                ) {
                  entry.history.forEach(historyItem => {
                    if (historyItem) {
                      const historyEntryId = `${entry.id ||
                        entry._id}_${historyItem.id ||
                        Math.random()
                          .toString(36)
                          .substr(2, 9)}`;

                      if (newProcessedIds.has(historyEntryId)) {
                        return;
                      }

                      newProcessedIds.add(historyEntryId);

                      const historyEntry = {
                        ...entry,
                        _id: historyEntryId,
                        id: historyEntryId,
                        status: historyItem.status || entry.status,
                        points: historyItem.points || 0,
                        totalPoints: historyItem.total || 0,
                        comments: historyItem.comments || "",
                        isHistoryItem: true,
                        historyItemId: historyItem.id || "",
                        parentEntryId: entry.id,
                        submitted: historyItem.created || entry.submitted,
                        reviewed: historyItem.reviewed || null,
                        reviewedBy:
                          historyItem.reviewedBy || entry.reviewedBy || ""
                      };

                      newExpandedEntries.push(historyEntry);
                    }
                  });
                } else {
                  if (!newProcessedIds.has(entry.id)) {
                    newProcessedIds.add(entry.id);
                    newExpandedEntries.push({
                      ...entry,
                      isHistoryItem: false
                    });
                  }
                }
              });

              commit("setCurrentOfferEntries", newExpandedEntries);
            })
            .catch(error => {
              console.error("Error refreshing entries:", error);
            });
        }
      });

      commit("setEntriesUnsubscribe", unsubscribe);
      return expandedEntries;
    } catch (error) {
      console.error("Error fetching offer entries:", error);
      commit("setOfferError", error.message);
      throw error;
    } finally {
      commit("setLoadingEntries", false);
    }
  },

  async createOffer({ commit, getters, dispatch }, payload) {
    if (!payload || !payload.title) {
      throw new Error("Offer data with title is required");
    }

    commit("setOfferError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const offersRef = collection(programRef, "offers");

      // Check for duplicate titles
      const titleUppercase = payload.title.toUpperCase();
      const titleDupQuery = query(
        offersRef,
        where("titleUppercase", "==", titleUppercase)
      );
      const titleDupSnapshot = await getDocs(titleDupQuery);

      if (!titleDupSnapshot.empty) {
        throw new Error("Offer title already exists");
      }

      const offer = {
        ...payload,
        titleUppercase,
        created: serverTimestamp(),
        updated: serverTimestamp(),
        createdBy: getters.currentUser?._id || "system",
        updatedBy: getters.currentUser?._id || "system",
        status: payload.status || "Draft",
        order: payload.order || 0,
        opportunities: payload.opportunities || [],
        memberTags: payload.memberTags || [],
        companyTags: payload.companyTags || []
      };

      const newOfferRef = await addDoc(offersRef, offer);

      const tempOffer = {
        ...offer,
        id: newOfferRef.id
        // Don't set created/updated here - they're already serverTimestamp() in the offer object
        // When we read them back, they'll be converted to Date objects
      };

      dispatch("setSnackbar", "Offer created successfully");
      return tempOffer;
    } catch (error) {
      console.error("Error creating offer:", error);
      commit("setOfferError", error.message);
      throw error;
    }
  },

  async updateOffer({ commit, getters, dispatch }, { id, updates }) {
    if (!id || !updates) {
      throw new Error("Offer ID and updates are required");
    }

    commit("setOfferError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const offerRef = doc(programRef, "offers", id);

      // Verify offer exists
      const offerDoc = await getDoc(offerRef);
      if (!offerDoc.exists()) {
        throw new Error("Offer not found");
      }

      // Check for duplicate titles if title is being updated
      if (updates.title) {
        const titleUppercase = updates.title.toUpperCase();
        const offersRef = collection(programRef, "offers");
        const titleDupQuery = query(
          offersRef,
          where("titleUppercase", "==", titleUppercase)
        );
        const titleDupSnapshot = await getDocs(titleDupQuery);

        if (!titleDupSnapshot.empty) {
          let duplicated = false;
          titleDupSnapshot.forEach(docSnapshot => {
            if (docSnapshot.id !== id) {
              duplicated = true;
            }
          });
          if (duplicated) {
            throw new Error("Offer title already exists");
          }
        }
        updates.titleUppercase = titleUppercase;
      }

      const updateData = {
        ...updates,
        updated: serverTimestamp(),
        updatedBy: getters.currentUser?._id || "system"
      };

      await updateDoc(offerRef, updateData);

      const updatedOffer = {
        ...offerDoc.data(),
        ...updates,
        id,
        // Don't set updated here - it's already serverTimestamp() in updateData
        updatedBy: getters.currentUser?._id || "system"
      };

      dispatch("setSnackbar", "Offer updated successfully");
      return updatedOffer;
    } catch (error) {
      console.error("Error updating offer:", error);
      commit("setOfferError", error.message);
      throw error;
    }
  },

  async deleteOffer({ commit, getters, dispatch }, offerId) {
    if (!offerId) {
      throw new Error("Offer ID is required");
    }

    commit("setOfferError", null);

    try {
      const programRef = doc(db(), "programs", getters.programId);
      const offerRef = doc(programRef, "offers", offerId);

      await deleteDoc(offerRef);

      dispatch("setSnackbar", "Offer deleted successfully");
      return true;
    } catch (error) {
      console.error("Error deleting offer:", error);
      commit("setOfferError", error.message);
      throw error;
    }
  },

  clearCurrentOffer({ commit }) {
    commit("setCurrentOffer", null);
  },

  clearCurrentOfferEntries({ commit }) {
    commit("setCurrentOfferEntries", []);
  },

  clearOfferError({ commit }) {
    commit("setOfferError", null);
  },

  cleanupEntriesListener({ state, commit }) {
    if (state.entriesUnsubscribe) {
      state.entriesUnsubscribe();
      commit("setEntriesUnsubscribe", null);
    }
  }
};

const mutations = {
  setOfferList(state, payload) {
    state.offerList = payload;
  },

  setCurrentOffer(state, payload) {
    state.currentOffer = payload;
  },

  setCurrentOfferEntries(state, entries) {
    // Deduplicate entries before storing them
    const uniqueEntriesMap = new Map();

    // Process entries to ensure uniqueness by _id or id
    entries.forEach(entry => {
      if (entry && (entry._id || entry.id)) {
        const key = entry._id || entry.id;
        uniqueEntriesMap.set(key, entry);
      }
    });

    // Convert the unique entries back to an array
    const uniqueEntries = Array.from(uniqueEntriesMap.values());

    state.currentOfferEntries = uniqueEntries;
  },

  setLoadingOffers(state, payload) {
    state.loadingOffers = Boolean(payload);
  },

  setLoadingCurrentOffer(state, payload) {
    state.loadingCurrentOffer = Boolean(payload);
  },

  setLoadingEntries(state, payload) {
    state.loadingEntries = Boolean(payload);
  },

  setProcessingOffer(state, payload) {
    state.processingOffer = Boolean(payload);
  },

  setOfferError(state, payload) {
    state.error = payload;
  },

  addEntryToCurrentOffer(state, entry) {
    if (state.currentOffer) {
      // Initialize entries array if it doesn't exist
      if (!state.currentOffer.entries) {
        state.currentOffer.entries = [];
      }

      // Add new entry to the entries array
      state.currentOffer.entries.push(entry);
    }
  },

  addEntryToCurrentOfferEntries(state, entry) {
    state.currentOfferEntries.unshift(entry);
  },

  setEntriesUnsubscribe(state, unsubscribe) {
    state.entriesUnsubscribe = unsubscribe;
  },

  setPendingOfferUpdates(state, updates) {
    state.pendingOfferUpdates = { ...updates };
  },

  clearPendingOfferUpdates(state) {
    state.pendingOfferUpdates = {};
  }
};

const getters = {
  offerList(state) {
    return state.offerList;
  },

  currentOffer(state) {
    return state.currentOffer;
  },

  currentOfferOpportunities(state) {
    return (state.currentOffer && state.currentOffer.opportunities) || [];
  },

  currentOfferEntries(state) {
    return state.currentOfferEntries;
  },

  getOfferById: state => id => {
    return (
      state.offerList.find(offer => offer.offerId === id || offer.id === id) ||
      null
    );
  },

  getOffersByStatus: state => status => {
    return state.offerList.filter(offer => offer.status === status);
  },

  activeOffers(state) {
    return state.offerList.filter(
      offer => offer.status === "Active" || offer.status === "active"
    );
  },

  offerGroups(state) {
    return state.offerList.filter(offer => offer.cardType === "offerGroup");
  },

  regularOffers(state) {
    return state.offerList.filter(offer => offer.cardType === "offer");
  },

  loadingOffers(state) {
    return state.loadingOffers;
  },

  loadingCurrentOffer(state) {
    return state.loadingCurrentOffer;
  },

  isLoadingEntries(state) {
    return state.loadingEntries;
  },

  processingOffer(state) {
    return state.processingOffer;
  },

  offerError(state) {
    return state.error;
  },

  offersIsLoading(state) {
    return (
      state.loadingOffers ||
      state.loadingCurrentOffer ||
      state.loadingEntries ||
      state.processingOffer
    );
  },

  offersCount(state) {
    return state.offerList.length;
  },

  activeOffersCount(state) {
    return state.offerList.filter(
      offer => offer.status === "Active" || offer.status === "active"
    ).length;
  },

  currentOfferEntriesCount(state) {
    return state.currentOfferEntries.length;
  },

  hasOffers(state) {
    return state.offerList.length > 0;
  },

  hasCurrentOfferEntries(state) {
    return state.currentOfferEntries.length > 0;
  },

  offersOptions(state) {
    return state.offerList.map(offer => ({
      text: offer.title || offer.displayTitle,
      value: offer.offerId || offer.id
    }));
  },

  activeOffersOptions(state) {
    return state.offerList
      .filter(offer => offer.status === "Active" || offer.status === "active")
      .map(offer => ({
        text: offer.title || offer.displayTitle,
        value: offer.offerId || offer.id
      }));
  },

  hasEntriesListener(state) {
    return !!state.entriesUnsubscribe;
  },

  pendingOfferUpdates(state) {
    return state.pendingOfferUpdates;
  },

  hasPendingOfferUpdates(state) {
    return Object.keys(state.pendingOfferUpdates).length > 0;
  }
};

// Helper function to clean form data for Firestore
function cleanFormDataForFirestore(formData) {
  if (!formData || typeof formData !== "object") {
    return {};
  }

  const cleanData = {};

  for (const [key, value] of Object.entries(formData)) {
    // Skip undefined values
    if (value === undefined) {
      continue;
    }

    // Handle primitives (string, number, boolean) and null
    if (value === null || typeof value !== "object") {
      cleanData[key] = value;
      continue;
    }

    // Handle arrays
    if (Array.isArray(value)) {
      cleanData[key] = value.map(item =>
        item && typeof item === "object"
          ? cleanFormDataForFirestore(item)
          : item
      );
      continue;
    }

    // Handle Firebase serverTimestamp objects - keep as-is
    if (value._methodName === "serverTimestamp") {
      cleanData[key] = value;
      continue;
    }

    // Handle Firebase timestamp objects - keep as-is
    if (value.seconds !== undefined || value.nanoseconds !== undefined) {
      cleanData[key] = value;
      continue;
    }

    // Handle Date objects
    if (value instanceof Date) {
      cleanData[key] = value.toISOString();
      continue;
    }

    // Handle objects - remove Vue Observers and other problematic properties
    if (typeof value === "object" && value !== null) {
      const cleanObject = {};

      for (const [nestedKey, nestedValue] of Object.entries(value)) {
        // Skip Vue Observer properties and other problematic properties
        if (nestedKey === "__ob__" || typeof nestedValue === "function") {
          continue;
        }

        // Skip validation and presets objects that have Vue Observers
        if (
          (nestedKey === "validation" || nestedKey === "presets") &&
          nestedValue &&
          nestedValue.__ob__
        ) {
          continue;
        }

        // Recursively clean nested objects
        if (nestedValue && typeof nestedValue === "object") {
          cleanObject[nestedKey] = cleanFormDataForFirestore(nestedValue);
        } else {
          cleanObject[nestedKey] = nestedValue;
        }
      }

      cleanData[key] = cleanObject;
      continue;
    }

    // Default case - keep the value
    cleanData[key] = value;
  }

  return cleanData;
}

export default {
  state,
  getters,
  actions,
  mutations
};

function isOfferVisibleToUser(offer, user) {
  // Check member tags
  let memberTagMatch = false;
  if (!offer.memberTags || offer.memberTags.length === 0) {
    memberTagMatch = true;
  } else {
    memberTagMatch = offer.memberTags.some(
      tag => user.tags && user.tags.includes(tag)
    );
  }

  // Check company tags
  let companyTagMatch = false;
  if (!offer.companyTags || offer.companyTags.length === 0) {
    companyTagMatch = true;
  } else {
    companyTagMatch = offer.companyTags.some(
      tag => user.companyTags && user.companyTags.includes(tag)
    );
  }

  return memberTagMatch && companyTagMatch;
}
